import { useEffect, useState } from 'react';
import api from '../../../services/api';
import type { Inspection } from '../../../types/api';

export interface AvailableShipperTag {
  tagName: string;
  displayName: string;
  color: string;
}

/**
 * Inspection-tagging state and handlers for the Inspections tab.
 *
 * Owns:
 *  - inspectionTags: map of inspectionId → tag names
 *  - availableShipperTags: shipper-defined tag palette (with colors)
 *  - bulk select mode + selection set
 *  - per-inspection inline tag-input toggle
 *  - tag filter
 *  - add/remove/bulk-tag handlers and the filteredInspections derivation
 */
export function useCarrierTabsTagging(dotNumber: number, inspections: Inspection[]) {
  const [inspectionTags, setInspectionTags] = useState<Record<string, string[]>>({});
  const [availableShipperTags, setAvailableShipperTags] = useState<AvailableShipperTag[]>([]);
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [selectedInspections, setSelectedInspections] = useState<Set<string>>(new Set());
  const [bulkTagging, setBulkTagging] = useState(false);
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  const [showTagInputFor, setShowTagInputFor] = useState<string | null>(null);

  useEffect(() => {
    if (dotNumber) {
      fetchInspectionTags();
      fetchAvailableShipperTags();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dotNumber]);

  const fetchInspectionTags = async () => {
    console.log('🔍 Fetching tags for carrier:', dotNumber);
    try {
      const response = await api.get(`/inspection-tags/carrier/${dotNumber}`);
      console.log('📡 Fetch tags response status:', response.status);
      const data = response.data;
      console.log('📦 Fetched tags data:', data);
      if (data.success) {
        setInspectionTags(data.data);
        console.log('✅ Tags set in state:', data.data);
      }
    } catch (error) {
      console.error('❌ Failed to fetch inspection tags:', error);
    }
  };

  const fetchAvailableShipperTags = async () => {
    try {
      const response = await api.get('/inspection-tags/shipper-tags/available');
      const data = response.data;
      if (data.success) {
        setAvailableShipperTags(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch available shipper tags:', error);
    }
  };

  const handleAddTag = async (inspectionId: string, tagName: string) => {
    console.log('🏷️ Adding tag:', { inspectionId, tagName, dotNumber });
    try {
      const response = await api.post('/inspection-tags', { inspectionId, dotNumber, tagName });
      console.log('📡 Response status:', response.status);
      const data = response.data;
      console.log('📦 Response data:', data);
      if (data.success) {
        console.log('✅ Tag added successfully, refreshing tags...');
        await fetchInspectionTags();
        console.log('✅ Tags refreshed!');
      } else {
        console.error('❌ Failed to add tag:', data.error);
        alert(`Failed to add tag: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ Error adding tag:', error);
      alert(`Error adding tag: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleRemoveTag = async (inspectionId: string, tagName: string) => {
    try {
      const response = await api.delete(`/inspection-tags/${inspectionId}/${encodeURIComponent(tagName)}`);
      const data = response.data;
      if (data.success) {
        await fetchInspectionTags();
      }
    } catch (error) {
      console.error('Failed to remove tag:', error);
    }
  };

  const handleBulkTag = async (tagName: string) => {
    if (selectedInspections.size === 0) return;

    setBulkTagging(true);
    try {
      const response = await api.post('/inspection-tags/bulk', {
        inspectionIds: Array.from(selectedInspections),
        dotNumber,
        tagName,
      });
      const data = response.data;
      if (data.success) {
        await fetchInspectionTags();
        setSelectedInspections(new Set());
        setBulkSelectMode(false);
      }
    } catch (error) {
      console.error('Failed to bulk tag:', error);
    } finally {
      setBulkTagging(false);
    }
  };

  const toggleInspectionSelection = (inspectionId: string) => {
    const newSelection = new Set(selectedInspections);
    if (newSelection.has(inspectionId)) {
      newSelection.delete(inspectionId);
    } else {
      newSelection.add(inspectionId);
    }
    setSelectedInspections(newSelection);
  };

  const getTagColor = (tagName: string): string => {
    const tag = availableShipperTags.find(t => t.tagName === tagName);
    return tag?.color || '#6b7280';
  };

  // Filter inspections by tag
  const filteredInspections = tagFilter
    ? inspections.filter(insp => inspectionTags[insp.inspectionId as string]?.includes(tagFilter))
    : inspections;

  return {
    inspectionTags,
    availableShipperTags,
    bulkSelectMode,
    setBulkSelectMode,
    selectedInspections,
    setSelectedInspections,
    bulkTagging,
    tagFilter,
    setTagFilter,
    showTagInputFor,
    setShowTagInputFor,
    handleAddTag,
    handleRemoveTag,
    handleBulkTag,
    toggleInspectionSelection,
    getTagColor,
    filteredInspections,
  };
}
